import { format } from 'date-fns';

/**
 * Shared date formatting function for chart axes and tooltips
 * Uses field descriptors for most fields, date formatting only for time-based fields
 */
export const isTimeField = (fieldKey: string) =>
  fieldKey === 'eventHour' ||
  fieldKey === 'eventDay' ||
  fieldKey === 'eventWeek' ||
  fieldKey === 'eventEndTime';

export const formatChartAxisLabel = (
  value: any,
  fieldKey?: string,
  fieldDescriptor?: any,
): string => {
  // Check if this is a time-based field that needs date formatting

  if (isTimeField(fieldKey)) {
    // Parse date if it's a string, or use directly if it's already a Date
    let date: Date | null = null;
    if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'string') {
      const parsed = new Date(value);
      date = !isNaN(parsed.getTime()) ? parsed : null;
    }

    // Check if we have a valid date
    const isValidDate = date instanceof Date && !isNaN(date.getTime());

    // Format date for time fields
    if (isValidDate) {
      return format(date, 'EE dd MMM HH:mm');
    }
  }

  // Use field descriptor formatter if available
  if (fieldDescriptor?.formatter) {
    try {
      const formatted = fieldDescriptor.formatter(value);
      return formatted?.toString() || String(value || '');
    } catch (e) {
      // Fallback if formatter fails
    }
  }

  // Final fallback to string (truncated if too long)
  const stringValue = String(value || '');
  return stringValue.length > 12
    ? stringValue.substring(0, 10) + '...'
    : stringValue;
};

/**
 * Parse and validate date for chart components
 */
export const parseChartDate = (
  rawDate: any,
): { date: Date | null; isValidDate: boolean } => {
  let date: Date | null = null;

  if (rawDate instanceof Date) {
    date = rawDate;
  } else if (typeof rawDate === 'string') {
    const parsed = new Date(rawDate);
    date = !isNaN(parsed.getTime()) ? parsed : null;
  }

  const isValidDate = date instanceof Date && !isNaN(date.getTime());

  return { date, isValidDate };
};

/**
 * Safe chart rendering utility with timeout protection
 */
export const withRenderTimeout = <T extends (...args: any[]) => any>(
  fn: T,
  timeoutMs: number = 5000,
): T => {
  return ((...args: any[]) => {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error(`Chart render timeout after ${timeoutMs}ms`));
      }, timeoutMs);

      try {
        const result = fn(...args);
        clearTimeout(timeout);
        resolve(result);
      } catch (error) {
        clearTimeout(timeout);
        reject(error);
      }
    });
  }) as T;
};

/**
 * Memory usage monitor for chart data
 */
export const estimateDataMemoryUsage = (data: any): number => {
  try {
    return JSON.stringify(data).length * 2; // Rough estimate in bytes
  } catch {
    return 0;
  }
};

/**
 * Check if current device/browser can handle large datasets
 */
export const getDeviceCapabilities = () => {
  const memory = (navigator as any)?.deviceMemory || 4; // GB, default to 4GB
  const hardwareConcurrency = navigator.hardwareConcurrency || 2;

  return {
    memory,
    cores: hardwareConcurrency,
    canHandleLargeDatasets: memory >= 8 && hardwareConcurrency >= 4,
    recommendedMaxDataPoints: memory < 4 ? 1000 : memory < 8 ? 5000 : 20000,
  };
};
